import * as React from 'react';

type Variant = 'primary' | 'ghost' | 'solid';
type Size = 'small' | 'medium' | 'large' | 'extra-large';

export interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  helper?: string;
  error?: string | null;
  name?: string;
  variant?: Variant;
  size?: Size;
  debounce?: number;
  onValidate?: (value: string) => string | null | Promise<string | null>;
  disabled?: boolean;
  color?: 'white' | 'black';
  resizable?: boolean;
}

const sizeClasses: Record<Size, string> = {
  small: 'px-2 py-1 text-sm',
  medium: 'px-3 py-2 text-sm',
  large: 'px-3 py-3 text-base',
  'extra-large': 'px-4 py-4 text-lg',
};

const baseTextareaClasses =
  'w-full  rounded text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500';

function cn(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  props,
  ref,
) {
  const {
    label,
    helper,
    error: errorProp,
    variant = 'primary',
    size = 'medium',
    debounce = 200,
    onValidate,
    className,
    value: valueProp,
    defaultValue,
    onChange,
    id,
    name,
    disabled,
    color = 'black',
    resizable = true,
    rows = 4,
    ...rest
  } = props;

  const isControlled = valueProp !== undefined;
  const [internalValue, setInternalValue] = React.useState<string>(
    (valueProp as string) ?? (defaultValue as string) ?? ''
  );
  const [error, setError] = React.useState<string | null>(errorProp ?? null);
  const [validating, setValidating] = React.useState(false);
  const idAttr = id ?? name ?? `textarea-${Math.random().toString(36).slice(2, 9)}`;

  React.useEffect(() => setError(errorProp ?? null), [errorProp]);

  React.useEffect(() => {
    if (isControlled) setInternalValue((valueProp as string) ?? '');
  }, [valueProp, isControlled]);

  const debouncedTimer = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    if (!onChange) return;
    if (debounce <= 0) return;
    window.clearTimeout(debouncedTimer.current);
    debouncedTimer.current = window.setTimeout(() => {
      const synthetic = ({ target: { value: internalValue, name } } as unknown) as React.ChangeEvent<HTMLTextAreaElement>;
      onChange(synthetic);
    }, debounce) as unknown as number;

    return () => {
      window.clearTimeout(debouncedTimer.current);
    };
  }, [internalValue, onChange, debounce, name]);

  const runValidation = React.useCallback(
    async (val: string) => {
      if (!onValidate) return null;
      try {
        setValidating(true);
        const res = await onValidate(val);
        setError(res ?? null);
        return res ?? null;
      } finally {
        setValidating(false);
      }
    },
    [onValidate]
  );

  const handleBlur = async (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (rest.onBlur) rest.onBlur(e);
    window.clearTimeout(debouncedTimer.current);
    await runValidation(internalValue);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInternalValue(val);
    if (debounce <= 0 && onChange) onChange(e);
    if (error) setError(null);

    if (onValidate) {
      runValidation(val);
    }
  };

  const wrapperClasses = cn(
    'relative',
    className
  );

  const finalTextareaClasses = cn(
    baseTextareaClasses,
    color === 'white' ? 'text-black bg-slate-50' : 'text-white bg-gray-800',
    sizeClasses[size],
    error ? 'border-red-500 ring-red-200' : 'border-gray-700',
    disabled ? 'opacity-50 cursor-not-allowed bg-gray-700' : '',
    !resizable ? 'resize-none' : 'resize-vertical'
  );

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={idAttr} className={`block text-sm font-medium mb-1 ${color === 'white' ? 'text-black' : 'text-white'}`}>
          {label}
        </label>
      )}

      <textarea
        id={idAttr}
        name={name}
        disabled={disabled}
        ref={ref}
        className={finalTextareaClasses}
        aria-invalid={!!error}
        aria-describedby={
          error ? `${idAttr}-error` : helper ? `${idAttr}-helper` : undefined
        }
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        rows={rows}
        {...rest}
      />

      {helper && !error && (
        <p id={`${idAttr}-helper`} className="mt-1 text-xs text-gray-400">
          {helper}
        </p>
      )}

      {error && (
        <p id={`${idAttr}-error`} className="mt-1 text-xs text-red-400">
          {error}
        </p>
      )}
    </div>
  );
});

export default Textarea;
